import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';

export type ReminderRecord = {
  id: string;
  title: string;
  description: string | null;
  due_at: string;
  status: 'pending' | 'done' | 'cancelled';
  notified_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_at: string | null;
  updated_at: string | null;
  plan_id: string | null;
  plan_title?: string | null;
  clients: {
    id: string;
    name: string;
    status: 'lead' | 'active' | 'past';
  };
};

async function fetchReminders(
  clientId: string,
  accessToken: string | null | undefined,
) {
  return apiRequest<ReminderRecord[]>(
    `/api/clients/${clientId}/reminders`,
    { method: 'GET' },
    accessToken,
  );
}

export function useClientReminders(clientId: string) {
  const { session } = useAuth();

  return useQuery<ReminderRecord[]>({
    queryKey: ['reminders', clientId],
    enabled: Boolean(clientId && session),
    queryFn: () => fetchReminders(clientId, session?.access_token),
  });
}

export function usePlanReminders(clientId: string, planId: string | null) {
  const { session } = useAuth();

  return useQuery<ReminderRecord[]>({
    queryKey: ['reminders', clientId, 'plan', planId],
    enabled: Boolean(clientId && planId && session),
    queryFn: () => {
      if (!planId) return Promise.resolve([]);
      return apiRequest<ReminderRecord[]>(
        `/api/clients/${clientId}/reminders?plan_id=${planId}`,
        { method: 'GET' },
        session?.access_token,
      );
    },
  });
}

export function useInvalidateReminders(clientId: string) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: ['reminders', clientId],
    });
}
